import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBrand } from '../contexts/BrandContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { formatApiError } from '../lib/utils';
import { Headphones, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const brand = useBrand();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const user = await login(email, password);
            toast.success('Welcome back!');
            if (user.role === 'admin') {
                navigate('/admin');
            } else if (user.role === 'operator') {
                navigate('/operator');
            } else {
                navigate('/my-guides');
            }
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0D14] flex">
            {/* Left Panel - Form */}
            <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16">
                <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-white mb-12 transition-colors">
                    <ArrowLeft className="w-4 h-4" />
                    Back to home
                </Link>

                <div className="max-w-md">
                    <div className="flex items-center gap-3 mb-8">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-9 max-w-[160px] object-contain" />
                        ) : (
                            <div className="w-10 h-10 rounded-md bg-gold flex items-center justify-center">
                                <Headphones className="w-5 h-5 text-[#0A0D14]" />
                            </div>
                        )}
                        {!brand.logo_url && <span className="font-outfit font-semibold text-xl text-white">{brand.platform_name}</span>}
                    </div>

                    <h1 className="font-outfit text-3xl font-bold text-white mb-2">Staff Sign In</h1>
                    <p className="text-muted-foreground mb-8">Admin and Tour Operator access</p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                className="bg-[#121620] border-white/10 focus:border-gold focus:ring-gold"
                                data-testid="login-email-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••"
                                required
                                className="bg-[#121620] border-white/10 focus:border-gold focus:ring-gold"
                                data-testid="login-password-input"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full bg-gold text-[#0A0D14] hover:bg-gold/90"
                            disabled={loading}
                            data-testid="login-submit-btn"
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </Button>
                    </form>

                    <p className="mt-6 text-center text-muted-foreground text-sm">
                        Traveler with a voucher code?{' '}
                        <Link to="/access" className="text-gold hover:underline">
                            Enter your code here
                        </Link>
                    </p>
                    <p className="mt-2 text-center text-muted-foreground text-sm">
                        New tour operator?{' '}
                        <Link to="/register/operator" className="text-gold hover:underline">
                            Register as a Partner
                        </Link>
                    </p>
                </div>
            </div>

            {/* Right Panel - Image */}
            <div className="hidden lg:block lg:w-1/2 relative">
                <img 
                    src="https://images.unsplash.com/photo-1688233923543-7b972d1b171a?w=1200"
                    alt="Traveler with headphones"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14] via-transparent to-transparent" />
            </div>
        </div>
    );
}
